import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { IEventModule } from "app/event/model"
import { getDateWithMaxTime, getDateWithMinTime } from "app/util/date/tools/common"
import { subDays, subMonths, subYears } from "date-fns"
import { IUIConfiguration } from "./configuration/model"

export const MIN_FILTER_LENGTH = 2

export const MAIN_LAYOUT_ID = "main-layout"
export const MAIN_LAYOUT_USER_MENU_BUTTON_ID: string = "main-layout-user-menu-button"
export const MAIN_LAYOUT_USER_MENU_ID: string = "main-layout-user-menu"
export const MENU_ITEM_ID: string = "menu-item"

export const MENU_CLOSED_WIDTH_IN_PX: number = 80
export const MENU_CLOSED_WIDTH_IN_REM: string = "5rem"
export const MENU_OPENED_WIDTH_IN_PX: number = 288
export const MENU_OPENED_WIDTH_IN_REM: string = "18rem"

export const FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS = 4
export const FLOATING_PANEL_HEADER_HEIGHT_IN_REM = "2rem"

export const MAX_PHONE_WIDTH_IN_PX: number = 480
export const MAX_TABLET_WIDTH_IN_PX: number = 768

export const USER_MENU_BUTTON_HEIGHT_IN_REM: string = "3.5rem"
export const PAGE_HEADER_HEIGHT_IN_REM: string = "4rem"
export const HEADER_PHONE_HEIGHT_IN_REM: string = "3rem"

export const APP_CONTAINER_ID: string = "app-container"

export const MAP_COLOR = "#d6674f"
export const THEME_PALETTE_SPECIAL_TEXT_COLOR = "#ffffff"
export const THEME_PALETTE_PRIMARY_COLOR = "#d6674f"

export const LIGHT_THEME_PALETTE_PRIMARY_DARK_COLOR = "#002B39"
export const LIGHT_THEME_PALETTE_SECONDARY_COLOR = "#b2b2b2"
export const LIGHT_THEME_PALETTE_BACKGROUND_DEFAULT = "#ffffff"
export const LIGHT_THEME_PALETTE_BACKGROUND_PAPER = "#ededed"
export const LIGHT_THEME_BOX_SHADOW = "0 4px 12px -4px rgba(0, 0, 0, 0.12)"
export const LIGHT_THEME_TEXT_PRIMARY = "#4b4b4b"
export const LIGHT_THEME_TEXT_SECONDARY = "#b2b2b2"
export const LIGHT_THEME_TEXT_PRIMARY2 = "#3e7384"
export const LIGHT_THEME_FORM = "#7c797c"
export const LIGHT_THEME_ERROR = "#d32f2f"
export const LIGHT_THEME_FORM_TEXT = "#ffffff"
export const LIGHT_THEME_BORDER = "1px solid #e0e0e0"
export const LIGHT_THEME_PALETTE_BORDER_DISABLED_COLOR = "#cbcbcb"

export const DARK_THEME_PALETTE_PRIMARY_DARK_COLOR = "#00273B"
export const DARK_THEME_PALETTE_SECONDARY_COLOR = "#6e6969"
export const DARK_THEME_PALETTE_BACKGROUND_DEFAULT = "#2f3037"
export const DARK_THEME_PALETTE_BACKGROUND_PAPER = "#222327"
export const DARK_THEME_BOX_SHADOW = "0 4px 12px -4px rgba(0, 0, 0, 60%)"
export const DARK_THEME_TEXT_PRIMARY = "#d5d5d5"
export const DARK_THEME_TEXT_SECONDARY = "#6e6969"
export const DARK_THEME_TEXT_PRIMARY2 = "#6badc3"
export const DARK_THEME_FORM = "#ffffff"
export const DARK_THEME_ERROR = "#d32f2f"
export const DARK_THEME_FORM_TEXT = "#00273B"
export const DARK_THEME_BORDER = "1px solid grey"
export const DARK_THEME_PALETTE_BORDER_DISABLED_COLOR = "#cbcbcb"
export const DARK_THEME_PALETTE_PRIMARY_LIGHT_COLOR = "#61c7e6"

export const NO_CREATION_DATE_FILTER: ITimeFilter = { date: null, labelKey: "label.no.filter" }

export const NO_FILTER_KEY = "label.no.filter"

export type SETTINGS_PAGES = "notification" | "agency" | "profile"
export const ALL_SETTINGS_PAGES: SETTINGS_PAGES[] = ["notification", "profile", "agency"]

const firstDayOfCurrentYear: Date = new Date()
firstDayOfCurrentYear.setMonth(0)
firstDayOfCurrentYear.setDate(1)
const START_END_FILTERS: IPeriodFilterItem[] = [
  { startDate: null, endDate: null, labelKey: NO_FILTER_KEY },
  { startDate: getDateWithMinTime(new Date()), endDate: getDateWithMaxTime(new Date()), labelKey: "today" },
  {
    startDate: getDateWithMinTime(subDays(new Date(), 1)),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "yesterday"
  },
  {
    startDate: getDateWithMinTime(subDays(new Date(), 7)),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "last.7.days"
  },
  {
    startDate: getDateWithMinTime(subDays(new Date(), 28)),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "last.28.days"
  },
  {
    startDate: getDateWithMinTime(subMonths(new Date(), 1)),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "last.1.month"
  },
  {
    startDate: getDateWithMinTime(subMonths(new Date(), 3)),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "last.3.months"
  },
  {
    startDate: getDateWithMinTime(subMonths(new Date(), 6)),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "last.6.months"
  },
  {
    startDate: getDateWithMinTime(subYears(new Date(), 1)),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "last.1.year"
  },
  {
    startDate: getDateWithMinTime(firstDayOfCurrentYear),
    endDate: getDateWithMaxTime(new Date()),
    labelKey: "last.current.year"
  }
]
export const getStartEndFilters = (): IPeriodFilterItem[] => START_END_FILTERS.slice()

export enum LIST_SORT_DIRECTIONS {
  DESC = "desc",
  ASC = "asc"
}

export enum UI_EVENTS {
  CONFIGURATION_CHANGE = "CONFIGURATION_CHANGE"
}

export enum UI_THEMES {
  DARK = "dark",
  LIGHT = "light"
}

export enum SCREEN_TYPES {
  DESKTOP = "desktop",
  TABLET = "tablet",
  PHONE = "phone"
}

// we define the module here instead of in type.d.ts because otherwise we have TypeScript compilation errors
// material-ui:
declare module "@mui/material/styles/createPalette" {
  interface Palette {
    border: React.CSSProperties["border"]
    form: React.CSSProperties["color"] // green/blue
    specialText: React.CSSProperties["color"]
    formText: React.CSSProperties["color"]
    boxShadow: React.CSSProperties["boxShadow"]
  }
  interface PaletteOptions {
    type: UI_THEMES
    border: React.CSSProperties["border"]
    form: React.CSSProperties["color"]
    specialText: React.CSSProperties["color"]
    formText: React.CSSProperties["color"]
    boxShadow: React.CSSProperties["boxShadow"]
  }
  interface TypeText {
    primary2: React.CSSProperties["color"]
  }
}

export interface ITimeFilter {
  date: Date | null
  labelKey: string
}

const CREATION_DATE_FILTERS: ITimeFilter[] = [
  NO_CREATION_DATE_FILTER,
  { date: subDays(new Date(), 1), labelKey: "less.than.1.day" },
  { date: subDays(new Date(), 15), labelKey: "less.than.15.days" },
  { date: subMonths(new Date(), 1), labelKey: "less.than.1.month" },
  { date: subMonths(new Date(), 3), labelKey: "less.than.3.months" },
  { date: subMonths(new Date(), 6), labelKey: "less.than.6.months" },
  { date: subYears(new Date(), 1), labelKey: "less.than.1.year" },
  { date: subYears(new Date(), 3), labelKey: "less.than.3.years" }
]
export const getCreationDateFilters = (): ITimeFilter[] => CREATION_DATE_FILTERS.slice()

export interface IUIEventModule extends IEventModule {
  on: {
    configurationChange(listenerId: string, fn: (configuration: IUIConfiguration) => void): void
  }
}

export interface IColorPickerPopupParams {
  title: string
  hexColor: string
  setHexColor: (color: string) => any
}

export interface IUIService {
  setDarkThemeActive(isDarkThemeActive: boolean, config?: IUIConfiguration): void
  isDarkThemeActive(): boolean
  openColorPickerPopup(params: IColorPickerPopupParams): void
  closeColorPickerPopup(): void
}

export interface IColorPickerPopupState extends IColorPickerPopupParams {
  isOpened: boolean
}

export interface IUiState {
  theme: { [key: string]: any }
  isMenuOpened: boolean
  menus: IMenuItem[]
  activeMenuPath: string
  container: {
    width: number
    height: number
  }
  contentWidth: number
  screenType: SCREEN_TYPES
  config: IUIConfiguration
  tempConfig: IUIConfiguration
  uiConfigUndoRedoHistory: IUIConfiguration[]
  uiConfigUndoRedoIndex: number
  isDarkPreview: boolean
  isSavingConfig: boolean
  colorPickerPopup: IColorPickerPopupState
}

export interface IMenuGroup {
  titleKey: string
  icon?: IconDefinition
  items: IMenu[]
  pagePaths: string[]
  pathParams?: string[]
}

export interface IMenu {
  titleKey: string
  icon: IconDefinition
  pagePaths: string[]
  pathParams?: string[]
}

export type IMenuItem = IMenuGroup | IMenu

export interface IOption {
  value: any
  label: string
}

export interface ISelectButtonItem {
  id: string
  label: string
  disabled?: boolean
  onClick: () => any
}

export interface IActionButton {
  label: string
  icon: IconDefinition
  id?: string
  disabled?: boolean
  onClick: () => any
}

export interface IUiConfigurationStateService {
  uiConfigUploadRefreshTimeout: any
}

export interface IPeriodFilter {
  startDate: Date | null
  endDate: Date | null
}

export interface IPeriodFilterItem extends IPeriodFilter {
  labelKey: string
}
